import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminSidebar from './AdminSidebar';
import AdminStatsCards from './AdminStatsCards';
import EnhancedAdminTableView from './EnhancedAdminTableView';
import AdminHeader from './AdminHeader';
import AdminSettings from './AdminSettings';
import LoadingSpinner from '../ui/Spinner';
import './AdminDashboard.css';
import API_BASE_URL from '../../config/api';

import { devLog } from '../../utils/devLog';
/**
 * Admin Dashboard - Main container for admin functionality
 * Provides comprehensive management interface for all system data
 */
const AdminDashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedTable, setSelectedTable] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Available tables for management - ALL MODELS
  const availableTables = [
    // Core User Management
    { key: 'users', label: 'Designers', icon: '👥', description: 'Designer accounts', category: 'User Management' },
    { key: 'user_profiles', label: 'Designer Profiles', icon: '👤', description: 'Designer profile details', category: 'User Management' },
    { key: 'customization_queries', label: 'Subscriptions', icon: '✂️', description: 'Designer subscription details', category: 'User Management' },

    // Customer Management
    { key: 'customer_users', label: 'Customers', icon: '🛒', description: 'Customer accounts', category: 'Customers' },
    { key: 'cart', label: 'Cart', icon: '🛍️', description: 'Customer shopping carts', category: 'Customers' },
    { key: 'checkout_details', label: 'Checkout Orders', icon: '💳', description: 'Customer checkout orders', category: 'Customers' },
    { key: 'bid_order', label: 'Bid Orders', icon: '🎯', description: 'Customer bid requests', category: 'Customers' },
    { key: 'reviews', label: 'Reviews', icon: '⭐', description: 'Product reviews', category: 'Customers' },
    { key: 'wishlist', label: 'Wishlist', icon: '❤️', description: 'Customer wishlists', category: 'Customers' },

    // Product Management
    { key: 'products', label: 'Products', icon: '🏷️', description: 'Designer products and listings', category: 'Products' },
    { key: 'fabrics', label: 'Fabrics', icon: '🧵', description: 'Fabric options and customizations', category: 'Products' },

    // Order Management
    { key: 'status', label: 'Order Status', icon: '📊', description: 'Order status tracking', category: 'Orders' },
    { key: 'received_orders', label: 'Received Orders', icon: '📦', description: 'Designer received orders', category: 'Orders' },
    { key: 'bidding_orders', label: 'Bidding Responses', icon: '💰', description: 'Designer bid responses', category: 'Orders' },
    { key: 'addresses', label: 'Addresses', icon: '📍', description: 'Shipping addresses', category: 'Orders' },
    { key: 'order_invoices', label: 'Invoices', icon: '📋', description: 'Order invoices and billing', category: 'Orders' },

    // Designer Management
    { key: 'designer_profiles', label: 'Payment Profiles', icon: '👗', description: 'Designer payment/onboarding data', category: 'Designers' },
    { key: 'designer_coupons', label: 'Coupons', icon: '🎫', description: 'Designer discount coupons', category: 'Designers' },
    { key: 'designer_notification_settings', label: 'Notifications', icon: '🔔', description: 'Designer notification preferences', category: 'Designers' },

    // Website & Templates
    { key: 'template_themes', label: 'Themes', icon: '🌈', description: 'Designer website themes', category: 'Templates' },
    { key: 'template_drafts', label: 'Drafts', icon: '📝', description: 'Auto-saved template drafts', category: 'Templates' },
    { key: 'template_configurations', label: 'Configurations', icon: '⚙️', description: 'Template configurations', category: 'Templates' },

    // Content & Communication
    { key: 'blog', label: 'Blog', icon: '📰', description: 'Blog posts', category: 'Content' },
    { key: 'designer_contact_messages', label: 'Contact Messages', icon: '✉️', description: 'Customer messages to designers', category: 'Content' },
    { key: 'newsletter_subscribers', label: 'Newsletter', icon: '📧', description: 'Newsletter subscribers', category: 'Content' },
    { key: 'home_email', label: 'Email List', icon: '📬', description: 'Collected email addresses', category: 'Content' },

    // System & Security
    { key: 'admin_audit_log', label: 'Audit Log', icon: '📜', description: 'Admin action history', category: 'Security' },
    { key: 'outstanding_tokens', label: 'Active Tokens', icon: '🔑', description: 'JWT token management', category: 'Security' },
    { key: 'blacklisted_tokens', label: 'Blacklisted Tokens', icon: '🚫', description: 'Revoked JWT tokens', category: 'Security' },
    { key: 'api_keys', label: 'API Keys', icon: '🗝️', description: 'API access keys', category: 'Security' },
    { key: 'log_entries', label: 'Admin Logs', icon: '📝', description: 'System activity logs', category: 'Security' },

    // Django System
    { key: 'permissions', label: 'Permissions', icon: '🔐', description: 'User permissions system', category: 'System' },
    { key: 'groups', label: 'User Groups', icon: '👥', description: 'User group management', category: 'System' },
    { key: 'content_types', label: 'Content Types', icon: '📄', description: 'Django content types', category: 'System' },
    { key: 'sessions', label: 'User Sessions', icon: '⏱️', description: 'Active user sessions', category: 'System' },
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      if (!token) {
        setError('Admin authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard-stats/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Admin access denied. You need admin privileges to access this dashboard.');
        } else {
          throw new Error('Failed to fetch dashboard statistics');
        }
        return;
      }

      const data = await response.json();
      setDashboardStats(data);
    } catch (err) {
      devLog.error('Error fetching dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (tableKey) => {
    setSelectedTable(tableKey);
    setActiveView('table');
  };

  const handleBackToOverview = () => {
    setActiveView('overview');
    setSelectedTable(null);
  };

  if (loading) {
    return (
      <div className="admin-dashboard admin-dashboard--loading">
        <LoadingSpinner size="large" />
        <p className="admin-dashboard__loading-text">Loading Admin Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard admin-dashboard--error">
        <div className="admin-error">
          <div className="admin-error__icon">🚫</div>
          <h2 className="admin-error__title">Access Denied</h2>
          <p className="admin-error__message">{error}</p>
          <button
            className="admin-error__button"
            onClick={() => window.location.href = '/'}
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-dashboard ${sidebarCollapsed ? 'admin-dashboard--sidebar-collapsed' : ''}`}>
      <AdminSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeView={activeView}
        onViewChange={setActiveView}
        availableTables={availableTables}
        onTableSelect={handleTableSelect}
        selectedTable={selectedTable}
      />

      <div className="admin-dashboard__main">
        <AdminHeader
          activeView={activeView}
          selectedTable={selectedTable}
          onBackToOverview={handleBackToOverview}
          availableTables={availableTables}
        />

        <main className="admin-dashboard__content">
          <AnimatePresence mode="wait">
            {activeView === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="admin-overview"
              >
                <div className="admin-overview__header">
                  <h1 className="admin-overview__title">
                    <span className="admin-overview__icon">⚡</span>
                    VASSTR Admin Dashboard
                  </h1>
                  <p className="admin-overview__subtitle">
                    Comprehensive system management and analytics
                  </p>
                </div>

                <AdminStatsCards stats={dashboardStats} />

                <div className="admin-overview__tables">
                  <h2 className="admin-overview__section-title">Data Management</h2>

                  {/* Group tables by category */}
                  {['User Management', 'Products', 'Orders', 'Designers', 'Templates', 'Security', 'System'].map((category) => {
                    const categoryTables = availableTables.filter(table => table.category === category);
                    if (categoryTables.length === 0) return null;

                    return (
                      <div key={category} className="admin-category-section">
                        <h3 className="admin-category-title">
                          <span className="admin-category-icon">
                            {category === 'User Management' && '👥'}
                            {category === 'Products' && '🛍️'}
                            {category === 'Orders' && '📦'}
                            {category === 'Designers' && '👗'}
                            {category === 'Templates' && '🎨'}
                            {category === 'Security' && '🔒'}
                            {category === 'System' && '⚙️'}
                          </span>
                          {category}
                        </h3>
                        <div className="admin-tables-grid">
                          {categoryTables.map((table) => (
                            <motion.div
                              key={table.key}
                              className="admin-table-card"
                              whileHover={{ y: -4, scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleTableSelect(table.key)}
                            >
                              <div className="admin-table-card__icon">{table.icon}</div>
                              <h4 className="admin-table-card__title">{table.label}</h4>
                              <p className="admin-table-card__description">{table.description}</p>
                              <div className="admin-table-card__arrow">→</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {activeView === 'table' && selectedTable && (
              <motion.div
                key={`table-${selectedTable}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="admin-table-view"
              >
                <EnhancedAdminTableView
                  tableName={selectedTable}
                  tableConfig={availableTables.find(t => t.key === selectedTable)}
                  onBack={handleBackToOverview}
                />
              </motion.div>
            )}

            {activeView === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="admin-settings"
              >
                <AdminSettings />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;